import React, {
  useState,
  useEffect,
  useContext,
  createContext,
  useCallback,
  useMemo,
} from 'react';
import api from 'services';
import { useAuth } from 'hooks/auth';
import { useNewToast } from 'hooks/newToast';
import { appWarnings } from 'utils/appWarnings';

const HelpContentsContext = createContext({});

export function HelpContentsProvider({ children }) {
  const { showToast } = useNewToast();
  const { isAuth } = useAuth();

  const [helpContents, setHelpContents] = useState([]);
  const [helpContentsPagined, setHelpContentsPagined] = useState({
    content: [],
    pagination: {},
  });
  const [helpContentsLoading, setHelpContentsLoading] = useState(false);

  const [helpContent, setHelpContent] = useState({});
  const [helpContentLoading, setHelpContentLoading] = useState(false);

  const [addHelpContentLoading, setAddHelpContentLoading] = useState(false);
  const [editHelpContentLoading, setEditHelpContentLoading] = useState(false);
  const [deleteHelpContentLoading, setDeleteHelpContentLoading] =
    useState(false);

  const down = useCallback(() => {
    setHelpContents([]);
    setHelpContentsPagined({ content: [], pagination: {} });
    setHelpContent({});
  }, []);

  const getHelpContents = useCallback(async () => {
    try {
      setHelpContentsLoading(true);
      const response = await api.helpContents().getHelpContents();
      setHelpContents(response);
    } catch {
    } finally {
      setHelpContentsLoading(false);
    }
  }, []);

  const getHelpContentsPagined = useCallback(async (filter) => {
    try {
      setHelpContentsLoading(true);
      const response = await api.helpContents().getHelpContents(filter);
      setHelpContentsPagined(response);
    } catch {
    } finally {
      setHelpContentsLoading(false);
    }
  }, []);

  const getHelpContent = useCallback(async (id, filter) => {
    try {
      setHelpContentLoading(true);
      const response = await api.helpContents().getHelpContent(id, filter);
      setHelpContent(response);
    } catch {
      setHelpContent({});
    } finally {
      setHelpContentLoading(false);
    }
  }, []);

  const addHelpContent = useCallback(
    async (data, refreshFilter) => {
      try {
        setAddHelpContentLoading(true);
        const formData = new FormData();

        formData.append('route', data.route);
        formData.append('title', data.title);
        formData.append('content', data.content);
        formData.append('type', data.type);
        if (data.video) {
          formData.append('video', data.video);
        }

        await api.helpContents().insertHelpContent(formData);
        showToast({
          type: 'success',
          message: 'Guia de ajuda criado com sucesso!',
        });

        await Promise.all([
          getHelpContents(),
          getHelpContentsPagined(refreshFilter),
        ]);
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setAddHelpContentLoading(false);
      }
    },
    [showToast, getHelpContents, getHelpContentsPagined],
  );

  const editHelpContent = useCallback(
    async (id, data, refreshFilter) => {
      try {
        setEditHelpContentLoading(true);
        const formData = new FormData();

        formData.append('route', data.route);
        formData.append('title', data.title);
        formData.append('content', data.content);
        formData.append('type', data.type);
        if (data.video) {
          formData.append('video', data.video);
        }

        await api.helpContents().editHelpContent(formData, id);
        showToast({
          type: 'success',
          message: 'Guia de ajuda atualizado com sucesso!',
        });

        await Promise.all([
          getHelpContents(),
          getHelpContentsPagined(refreshFilter),
        ]);
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setEditHelpContentLoading(false);
      }
    },
    [showToast, getHelpContents, getHelpContentsPagined],
  );

  const deleteHelpContent = useCallback(
    async (id, refreshFilter) => {
      try {
        setDeleteHelpContentLoading(true);
        await api.helpContents().deleteHelpContent(id);
        showToast({
          type: 'success',
          message: 'Guia de ajuda removido com sucesso!',
        });

        await Promise.all([
          getHelpContents(),
          getHelpContentsPagined(refreshFilter),
        ]);
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setDeleteHelpContentLoading(false);
      }
    },
    [showToast, getHelpContents, getHelpContentsPagined],
  );

  useEffect(() => {
    if (!isAuth) {
      down();
    }
  }, [isAuth, down]);

  const value = useMemo(
    () => ({
      helpContents,
      helpContentsPagined,
      helpContent,
      helpContentsLoading,
      helpContentLoading,
      addHelpContentLoading,
      editHelpContentLoading,
      deleteHelpContentLoading,
      getHelpContents,
      getHelpContentsPagined,
      getHelpContent,
      addHelpContent,
      editHelpContent,
      deleteHelpContent,
    }),
    [
      helpContents,
      helpContentsPagined,
      helpContent,
      helpContentsLoading,
      helpContentLoading,
      addHelpContentLoading,
      editHelpContentLoading,
      deleteHelpContentLoading,
      getHelpContents,
      getHelpContentsPagined,
      getHelpContent,
      addHelpContent,
      editHelpContent,
      deleteHelpContent,
    ],
  );

  return (
    <HelpContentsContext.Provider value={value}>
      {children}
    </HelpContentsContext.Provider>
  );
}

export function useHelpContents() {
  const context = useContext(HelpContentsContext);

  if (!context) {
    throw new Error(
      'useHelpContents must be used within a HelpContentsProvider',
    );
  }

  return context;
}
